import { Entity, IEntityTransformer, Key } from '@briebug/ngrx-auto-entity';
import { RequireId } from '../../../shared/utils/types';

/**
 * TT-287 (Ben, 11/5). Pictures are matched on the discipline's name first, as
 * the VTI has done since TT-215. The id alone stopped being enough once
 * Resilient was split into Modular and Sheet and Inspection was added: the new
 * ones get whatever id the database hands out, so they had no picture here.
 *
 * The id map below is kept as a fallback so anything not named here keeps the
 * picture it has today.
 */
const IMAGE_BY_TYPE: Record<string, string> = {
  carpet: '/assets/images/carpet-feature.jpg',
  'ceramic tile': '/assets/images/ceramic-feature.jpg',
  // The two halves of the old "Resilient", as on the VTI: Sheet keeps the
  // Resilient picture, and Modular gets the newer flooring one.
  'resilient sheet': '/assets/images/resilient-feature.jpg',
  'resilient modular': '/assets/images/resilient-modular-feature.jpg',
  wood: '/assets/images/wood-feature.jpg',
  'concrete treatments': '/assets/images/concrete-feature.jpg',
  inspection: '/assets/images/inspection-feature.jpg',
};

const PRODUCT_INSTALLATION_TYPE_META_DATA: Record<number, { imageUrl: string }> = {
  1: { imageUrl: '/assets/images/carpet-feature.jpg' },
  2: { imageUrl: '/assets/images/ceramic-feature.jpg' },
  3: { imageUrl: '/assets/images/resilient-feature.jpg' },
  4: { imageUrl: '/assets/images/wood-feature.jpg' },
  5: { imageUrl: '/assets/images/services-feature.jpg' },
  6: { imageUrl: '/assets/images/concrete-feature.jpg' },
  7: { imageUrl: '/assets/images/floor-care-feature.jpg' },
};

// Lower-cased and trimmed, with hyphens read as spaces, because the database
// spells the halves "Resilient-Sheet" and "Resilient-Modular".
export const imageForProductInstallationType = (
  type: string | undefined,
  id: number | undefined,
): string | undefined =>
  IMAGE_BY_TYPE[(type ?? '').trim().toLowerCase().replace(/[\s-]+/g, ' ')] ??
  (id === undefined ? undefined : PRODUCT_INSTALLATION_TYPE_META_DATA[id]?.imageUrl);

export const attachImage: IEntityTransformer = {
  fromServer: <T extends RequireId<ProductInstallationType>>(data: T) => (
    (data.imageUrl = imageForProductInstallationType(data.type, data.id)), data
  ),
};

@Entity({
  modelName: 'ProductInstallationType',
  uriName: 'product-installation-types',
  transform: [attachImage],
})
export class ProductInstallationType {
  @Key id?: number;
  type!: string;
  description?: string;
  imageUrl?: string;
}
